import React, { useState, useEffect } from 'react';
import { 
  Calendar, Clock, Plus, Trash2, X, CheckCircle, XCircle, 
  RefreshCw, Settings, User, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const DAYS_SHORT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

export default function AppointmentsDashboard({ token }) {
  const [appointments, setAppointments] = useState([]);
  const [availability, setAvailability] = useState({ slots: [], blocked_dates: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('calendar');
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [showBlockDate, setShowBlockDate] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDayDetail, setShowDayDetail] = useState(false);
  
  // Calendar navigation
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  
  // New slot form
  const [newSlot, setNewSlot] = useState({
    day_of_week: 0,
    start_time: '09:00',
    end_time: '12:00',
    slot_duration: 30
  });
  
  // Block date form
  const [blockDate, setBlockDate] = useState({ date: '', reason: '' });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAppointments = async () => {
    try {
      const res = await axios.get(`${API}/admin/appointments`, { headers });
      setAppointments(res.data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  const fetchAvailability = async () => {
    try {
      const res = await axios.get(`${API}/admin/appointments/availability`, { headers });
      setAvailability(res.data);
    } catch (error) {
      console.error('Error fetching availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const addSlot = async () => {
    try {
      await axios.post(`${API}/admin/appointments/availability`, null, {
        headers,
        params: {
          day_of_week: newSlot.day_of_week,
          start_time: newSlot.start_time,
          end_time: newSlot.end_time,
          slot_duration: newSlot.slot_duration
        }
      });
      toast.success('Créneau ajouté');
      setShowAddSlot(false);
      fetchAvailability();
    } catch (error) {
      toast.error("Erreur lors de l'ajout du créneau");
    }
  };

  const deleteSlot = async (slotId) => {
    try {
      await axios.delete(`${API}/admin/appointments/availability/${slotId}`, { headers });
      toast.success('Créneau supprimé');
      fetchAvailability();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const addBlockedDate = async () => {
    try {
      await axios.post(`${API}/admin/appointments/block-date`, null, {
        headers,
        params: { date: blockDate.date, reason: blockDate.reason }
      });
      toast.success('Date bloquée');
      setShowBlockDate(false);
      setBlockDate({ date: '', reason: '' });
      fetchAvailability();
    } catch (error) {
      toast.error('Erreur lors du blocage de la date');
    }
  };

  const unblockDate = async (date) => {
    try {
      await axios.delete(`${API}/admin/appointments/block-date/${date}`, { headers });
      toast.success('Date débloquée');
      fetchAvailability();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const updateAppointmentStatus = async (appointmentId, status) => {
    try {
      await axios.put(`${API}/admin/appointments/${appointmentId}/status`, null, {
        headers,
        params: { status }
      });
      toast.success(`RDV ${status === 'completed' ? 'terminé' : 'annulé'}`);
      fetchAppointments();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  useEffect(() => {
    fetchAppointments();
    fetchAvailability();
  }, []);

  // Calendar helpers
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; // Convert to Monday-first
  };

  const formatDateStr = (day, month, year) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const getDateStatus = (day) => {
    const dateStr = formatDateStr(day, currentMonth, currentYear);
    const date = new Date(currentYear, currentMonth, day);
    const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1;
    
    // Check if date is blocked
    const isBlocked = availability.blocked_dates?.some(b => b.date === dateStr);
    if (isBlocked) return { status: 'blocked', color: 'bg-gray-200', text: '⛔' };
    
    // Check if day has availability slots
    const daySlots = availability.slots?.filter(s => s.day_of_week === dayOfWeek && s.is_active) || [];
    if (daySlots.length === 0) return { status: 'unavailable', color: 'bg-gray-100', text: '' };
    
    // Count appointments for this date
    const dayAppointments = appointments.filter(a => a.date === dateStr && a.status !== 'cancelled');
    
    // Calculate total available slots for this day
    let totalSlots = 0;
    daySlots.forEach(slot => {
      const start = parseInt(slot.start_time.split(':')[0]) * 60 + parseInt(slot.start_time.split(':')[1]);
      const end = parseInt(slot.end_time.split(':')[0]) * 60 + parseInt(slot.end_time.split(':')[1]);
      const duration = slot.slot_duration || 30;
      totalSlots += Math.floor((end - start) / duration);
    });
    
    if (dayAppointments.length === 0) {
      return { status: 'available', color: 'bg-emerald-100 hover:bg-emerald-200', text: '🟢', appointments: [], totalSlots };
    } else if (dayAppointments.length >= totalSlots) {
      return { status: 'full', color: 'bg-red-100', text: '🔴', appointments: dayAppointments, totalSlots };
    } else {
      return { status: 'partial', color: 'bg-amber-100 hover:bg-amber-200', text: '🟡', appointments: dayAppointments, totalSlots };
    }
  };

  const handleDayClick = (day) => {
    const dateStr = formatDateStr(day, currentMonth, currentYear);
    const status = getDateStatus(day);
    if (status.status !== 'unavailable') {
      setSelectedDate({
        date: dateStr,
        day,
        month: currentMonth,
        year: currentYear,
        ...status
      });
      setShowDayDetail(true);
    }
  };

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const upcomingAppointments = appointments.filter(a => {
    const appointmentDate = new Date(a.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return appointmentDate >= today && a.status !== 'cancelled';
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-navy/50" />
      </div>
    );
  }

  // Generate calendar days
  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
  const calendarDays = [];
  
  // Empty cells before first day
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }
  // Days of month
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push(i);
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-4 border-b pb-4">
        <Button
          variant={activeTab === 'calendar' ? 'default' : 'outline'}
          onClick={() => setActiveTab('calendar')}
          className={activeTab === 'calendar' ? 'bg-gold text-black' : ''}
        >
          <Calendar className="w-4 h-4 mr-2" />
          Calendrier
        </Button>
        <Button
          variant={activeTab === 'list' ? 'default' : 'outline'}
          onClick={() => setActiveTab('list')}
        >
          <Clock className="w-4 h-4 mr-2" />
          Liste RDV ({upcomingAppointments.length})
        </Button>
        <Button
          variant={activeTab === 'settings' ? 'default' : 'outline'}
          onClick={() => setActiveTab('settings')}
        >
          <Settings className="w-4 h-4 mr-2" />
          Disponibilités
        </Button>
      </div>

      {/* CALENDAR VIEW */}
      {activeTab === 'calendar' && (
        <div className="space-y-4">
          {/* Legend */}
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-emerald-100 flex items-center justify-center text-xs">🟢</span>
              <span>Disponible</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-amber-100 flex items-center justify-center text-xs">🟡</span>
              <span>Partiellement réservé</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-red-100 flex items-center justify-center text-xs">🔴</span>
              <span>Complet</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center text-xs">⛔</span>
              <span>Bloqué</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-gray-100"></span>
              <span>Non disponible</span>
            </div>
          </div>

          {/* Calendar Grid */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={prevMonth}>
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <h3 className="font-heading text-xl font-bold text-navy">
                  {MONTHS[currentMonth]} {currentYear}
                </h3>
                <Button variant="ghost" size="sm" onClick={nextMonth}>
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {DAYS_SHORT.map(day => (
                  <div key={day} className="text-center text-sm font-semibold text-navy/60 py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, index) => {
                  if (day === null) {
                    return <div key={`empty-${index}`} className="h-20"></div>;
                  }
                  
                  const status = getDateStatus(day);
                  const isToday = day === new Date().getDate() && 
                                  currentMonth === new Date().getMonth() && 
                                  currentYear === new Date().getFullYear();
                  const isPast = new Date(currentYear, currentMonth, day) < new Date(new Date().setHours(0,0,0,0));
                  
                  // Get first appointment time for display
                  const firstAppointment = status.appointments?.[0];
                  const appointmentTime = firstAppointment?.time_slot?.split('-')[0]; // "10:00-10:30" -> "10:00"
                  
                  return (
                    <button
                      key={day}
                      onClick={() => !isPast && handleDayClick(day)}
                      disabled={isPast || status.status === 'unavailable'}
                      className={`h-20 rounded-lg border transition-all flex flex-col items-center justify-center p-1
                        ${status.color}
                        ${isToday ? 'ring-2 ring-gold' : ''}
                        ${isPast ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                        ${status.status === 'unavailable' ? 'cursor-default' : ''}
                      `}
                    >
                      <span className={`text-lg font-semibold ${isToday ? 'text-gold' : 'text-navy'}`}>
                        {day}
                      </span>
                      {status.text && <span className="text-xs">{status.text}</span>}
                      {status.appointments?.length > 0 && (
                        <>
                          <span className="text-xs font-bold text-gold">{appointmentTime}</span>
                          {status.appointments.length > 1 && (
                            <span className="text-xs text-navy/60">+{status.appointments.length - 1} autre(s)</span>
                          )}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* LIST VIEW */}
      {activeTab === 'list' && (
        <div className="space-y-6">
          <div>
            <h3 className="font-heading text-lg font-bold text-navy mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gold" />
              Prochains RDV
            </h3>
            {upcomingAppointments.length === 0 ? (
              <Card className="bg-gray-50">
                <CardContent className="p-6 text-center text-muted-foreground">
                  Aucun rendez-vous à venir
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {upcomingAppointments.map((apt) => (
                  <Card key={apt.id} className="border-l-4 border-l-gold">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-navy/10 flex items-center justify-center">
                            <User className="w-6 h-6 text-navy" />
                          </div>
                          <div>
                            <p className="font-semibold text-navy">{apt.client_name}</p>
                            <p className="text-sm text-muted-foreground">{apt.client_email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-navy">{formatDate(apt.date)}</p>
                          <p className="text-gold font-medium text-lg">{apt.time_slot}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-600 border-emerald-300"
                            onClick={() => updateAppointmentStatus(apt.id, 'completed')}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Terminé
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300"
                            onClick={() => updateAppointmentStatus(apt.id, 'cancelled')}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Annuler
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* SETTINGS VIEW */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Availability Slots */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-navy flex items-center gap-2">
                <Clock className="w-5 h-5 text-gold" />
                Créneaux de disponibilité
              </h3>
              <Button onClick={() => setShowAddSlot(true)}>
                <Plus className="w-4 h-4 mr-1" />
                Ajouter un créneau
              </Button>
            </div>

            <div className="grid gap-4">
              {DAYS.map((day, dayIndex) => {
                const daySlots = availability.slots.filter(s => s.day_of_week === dayIndex);
                return (
                  <Card key={day} className={daySlots.length > 0 ? 'border-emerald-200' : 'bg-gray-50'}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-navy">{day}</h4>
                        <div className="flex gap-2 flex-wrap">
                          {daySlots.length === 0 ? (
                            <Badge variant="outline" className="text-muted-foreground">Non disponible</Badge>
                          ) : (
                            daySlots.map((slot) => (
                              <Badge 
                                key={slot.id} 
                                className="bg-emerald-100 text-emerald-700 flex items-center gap-1"
                              >
                                {slot.start_time} - {slot.end_time}
                                <button 
                                  onClick={() => deleteSlot(slot.id)}
                                  className="ml-1 hover:text-red-500"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </Badge>
                            ))
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Blocked Dates */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-navy flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-500" />
                Dates bloquées
              </h3>
              <Button variant="outline" onClick={() => setShowBlockDate(true)}>
                <Plus className="w-4 h-4 mr-1" />
                Bloquer une date
              </Button>
            </div>

            {availability.blocked_dates.length === 0 ? (
              <Card className="bg-gray-50">
                <CardContent className="p-4 text-center text-muted-foreground">
                  Aucune date bloquée
                </CardContent>
              </Card>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availability.blocked_dates.map((blocked) => (
                  <Badge 
                    key={blocked.date} 
                    className="bg-red-100 text-red-700 flex items-center gap-1 py-2"
                  >
                    {formatDate(blocked.date)}
                    {blocked.reason && ` (${blocked.reason})`}
                    <button 
                      onClick={() => unblockDate(blocked.date)}
                      className="ml-1 hover:text-red-900"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Day Detail Dialog */}
      <Dialog open={showDayDetail} onOpenChange={setShowDayDetail}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gold" />
              {selectedDate && formatDate(selectedDate.date)}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedDate?.status === 'blocked' ? (
              <div className="text-center py-4">
                <p className="text-red-600 font-medium">⛔ Cette date est bloquée</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => {
                    unblockDate(selectedDate.date);
                    setShowDayDetail(false);
                  }}
                >
                  Débloquer cette date
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-muted-foreground">Créneaux disponibles</span>
                  <span className="font-semibold text-navy">{selectedDate?.totalSlots || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-muted-foreground">RDV réservés</span>
                  <span className="font-semibold text-navy">{selectedDate?.appointments?.length || 0}</span>
                </div>
                
                {selectedDate?.appointments?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-navy">RDV de ce jour :</h4>
                    {selectedDate.appointments.map((apt) => (
                      <div key={apt.id} className="flex items-center justify-between p-3 bg-gold/10 rounded-lg border border-gold/30">
                        <div>
                          <p className="font-medium text-navy">{apt.client_name}</p>
                          <p className="text-xs text-muted-foreground">{apt.client_email}</p>
                        </div>
                        <Badge className="bg-gold text-black">{apt.time_slot}</Badge>
                      </div>
                    ))}
                  </div>
                )}
                
                <Button 
                  variant="outline" 
                  className="w-full text-red-600 border-red-300"
                  onClick={() => {
                    setBlockDate({ date: selectedDate.date, reason: '' });
                    setShowDayDetail(false);
                    setShowBlockDate(true);
                  }}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Bloquer cette date
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Slot Dialog */}
      <Dialog open={showAddSlot} onOpenChange={setShowAddSlot}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un créneau</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Jour</Label>
              <select
                className="w-full p-2 border rounded-md mt-1"
                value={newSlot.day_of_week}
                onChange={(e) => setNewSlot({ ...newSlot, day_of_week: parseInt(e.target.value) })}
              >
                {DAYS.map((day, index) => (
                  <option key={day} value={index}>{day}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Heure début</Label>
                <Input
                  type="time"
                  value={newSlot.start_time}
                  onChange={(e) => setNewSlot({ ...newSlot, start_time: e.target.value })}
                />
              </div>
              <div>
                <Label>Heure fin</Label>
                <Input
                  type="time"
                  value={newSlot.end_time}
                  onChange={(e) => setNewSlot({ ...newSlot, end_time: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Durée d'un RDV (minutes)</Label>
              <select
                className="w-full p-2 border rounded-md mt-1"
                value={newSlot.slot_duration}
                onChange={(e) => setNewSlot({ ...newSlot, slot_duration: parseInt(e.target.value) })}
              >
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={45}>45 min</option>
                <option value={60}>1 heure</option>
              </select>
            </div>
            <Button className="w-full" onClick={addSlot}>
              Ajouter le créneau
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Block Date Dialog */}
      <Dialog open={showBlockDate} onOpenChange={setShowBlockDate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bloquer une date</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Date à bloquer</Label>
              <Input
                type="date"
                value={blockDate.date}
                onChange={(e) => setBlockDate({ ...blockDate, date: e.target.value })}
              />
            </div>
            <div>
              <Label>Raison (optionnel)</Label>
              <Input
                value={blockDate.reason}
                onChange={(e) => setBlockDate({ ...blockDate, reason: e.target.value })}
                placeholder="Ex: Vacances, Formation..."
              />
            </div>
            <Button className="w-full" onClick={addBlockedDate}>
              Bloquer cette date
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
